import Footer from "components/layout/footer";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import logo from "icons/smallLogo.svg";
import ScannerImg from "../../icons/bi_qr-code.png";
import {
  calculateDiffSeconds,
  decode,
  formatCurrency,
  isExpired,
} from "utils/HelperFunctions";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import QuotesList from "components/actionCenter/QuotesList";
import InvoicePreview from "components/actionCenter/InvoicePreview";
import Drawer from "components/ui-component/SideDrawer";
import {
  emailAcceptQuotes,
  emailDeclineQuotes,
  quotesDetailsAccept,
  quotesDetailsDecline,
  quotesDetailsGet,
} from "store/slices/subscriptionSlice";
import { useMediaQuery } from "react-responsive";
import { updateLogoAndFavicon } from "store/slices/paymentsSlice";
import moment from "moment";
import { RootState } from "store/store";
import { formatPaymentListDate, formatDisplayDate } from "utils/DateFormat";
import Countdown from "components/ui-component/Countdown";
import { useCountdown } from "hooks/useCountdown";
import { BASE_URL } from "store/api/constApi";
import { useIsLogin } from "store/slices/commonSlice";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";
import MessageToast from "components/ui-component/MessageToast";
import {
  linkAccountMismatchMessage,
  signOutOtherAccount,
  takeLinkAccountMismatch,
} from "utils/linkAccountMismatch";
import { goToInvalid } from "utils/invalidRoute";
import { isInvalidPublicLinkResult } from "utils/publicLinkValidation";

const QuotesContent = () => {
  const testAttrs = useTestAttrsFactory();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [quoteData, setQuoteData] = useState<any>();
  const [timeDiff, setTimeDiff] = useState<number | null>(null);
  const seconds = useCountdown(timeDiff);
  const [invoiceId, setInvoiceId] = useState<any>();
  const [qrCode, setQrCode] = useState<any>();
  const [acceptQuote, setAcceptQuote] = useState<string | null>(null);
  const [isFutureSub, setIsFutureSub] = useState<boolean>(false);
  const { id: actionId, type } = useParams();
  const navigate = useNavigate();
  const userData = useUserData();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<any>("");
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const [isAcceptLoading, setIsAcceptLoading] = useState<boolean>(false);
  const [isDeclineLoading, setIsDeclineLoading] = useState<boolean>(false);
  const [paymentLogo, setPaymentLogo] = useState<any>(logo);

  const dispatch = useAppDispatch();
  const isLogin = useIsLogin();

  useEffect(() => {
    if (isLogin && quoteData && userData) {
      const accounts_id = quoteData?.accounts_id;
      const id = userData?.data?.accountData?.id;

      if (id && accounts_id && id !== accounts_id) {
        signOutOtherAccount(dispatch);
      }
    }
  }, [quoteData, isLogin, userData]);

  // After signing out a different account, say why the page reloaded.
  const [mismatchNotice, setMismatchNotice] = useState<string | null>(null);
  useEffect(() => {
    if (takeLinkAccountMismatch()) {
      setMismatchNotice(linkAccountMismatchMessage("quote"));
    }
  }, []);

  const getQuoteData = async () => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let payload = {
        slug: actionId,
      };
      let res: any = await dispatch(quotesDetailsGet(payload)).unwrap();
      if (isInvalidPublicLinkResult(res)) {
        goToInvalid(navigate, "link");
        return;
      }
      if (res.body.success) {
        const { customerData, companyLogoDetails, quoteData, qrcode } =
          res?.body?.data;
        setQuoteData(quoteData);

        const diff = calculateDiffSeconds(new Date(), quoteData?.expiry_date);
        setTimeDiff(parseInt(diff?.toFixed(0)));

        setQrCode(qrcode);
        setPaymentLogo(companyLogoDetails?.logo);
        dispatch(updateLogoAndFavicon(companyLogoDetails));
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData,
          })
        );
        if (quoteData?.status_quote?.toLowerCase() === "expired") {
          setAcceptQuote("expired");
        }
        if (quoteData?.status_quote?.toLowerCase() === "cancelled") {
          setAcceptQuote("cancelled");
        } else if (
          quoteData?.status_quote?.toLowerCase() !== "accepted" &&
          quoteData?.status_quote?.toLowerCase() !== "declined"
        ) {
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "quote",
              userid: customerData?.leadData?.user_id,
              email: "",
              event_status: "clicked",
              activity_type: "quote_activity",
              clusterid: slug[1],
              communication_status: "",
              accounts_id: quoteData?.accounts_id,
              payment_amount: quoteData?.total_amount,
              quote_id: quoteData?.id,
            })
          );
        } else {
          if (moment().isBefore(moment(quoteData?.subs_start_date))) {
            setIsFutureSub(true);
          }
          setAcceptQuote(
            quoteData?.status_quote?.toLowerCase() !== "accepted"
              ? "decline"
              : "accept"
          );
        }
      }
    } catch (error) {
      if (isInvalidPublicLinkResult(error)) {
        goToInvalid(navigate, "link");
        return;
      }
      console.log("getQuoteData error =====", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!actionId) return;
    const requestOptions = {
      method: "GET",
      redirect: "follow",
    };

    fetch(
      `${BASE_URL}/portal/get-payment-status?slug=${actionId}&type=quote`,
      requestOptions as any
    )
      .then((response) =>
        handleStreamResponse(response, (res: any) => {
          const match = res.match(
            /"success":\s*(true|false).*?"invoice_slug":"(.*?)".*?"isFutureSub":\s*(true|false)/
          );

          if (match) {
            if (match[1] === "true") {
              setAcceptQuote("accept");
              setInvoiceId(match[2]);
              setIsFutureSub(match[3] === "true");
            }
          }
        })
      )
      .then((result) => console.log(result))
      .catch((error) => console.error(error));
  }, [actionId]);

  const handleStreamResponse = async (
    response: any,
    onSuccessCallback: any
  ) => {
    const reader = response.body.getReader();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log("Stream finished.");
          break;
        }
        const chunk: any = new TextDecoder().decode(value);

        onSuccessCallback(chunk);
      }
    } catch (error) {
      console.error("Error reading the stream", error);
      // isConnectionClosed(true); // Assume the connection is closed on error
    }
  };

  useEffect(() => {
    if (actionId) {
      getQuoteData();
    }
  }, [actionId]);

  const OnSubmit = async (value: string) => {
    let slug = decode(actionId || "")?.split("_");
    try {
      let params = {
        id: actionId,
        clusterid: slug[1],
        accounts_id: userData?.data?.accountData?.id,
        lead_owner: userData?.data?.accountData?.lead_owner,
      };
      switch (value) {
        case "accept":
          setIsAcceptLoading(true);
          let res: any = await dispatch(quotesDetailsAccept(params)).unwrap();
          if (res?.body?.success) {
            setInvoiceId(res?.body?.data?.invoice_id);
            setIsFutureSub(res?.body?.data?.isFutureSub || false);

            setAcceptQuote(value);
            let payload = {
              email: userData?.data?.accountData?.email,
              clusterid: params.clusterid,
              invoice_slug: res?.body?.data?.invoice_id,
              invoice_id: res?.body?.data?.invoice_pid,
            };

            if (res?.body?.data?.wireDetails) {
              payload = { ...payload, ...res?.body?.data?.wireDetails };
            }

            if (!res?.body?.data?.isFutureSub) {
              await dispatch(emailAcceptQuotes(payload));
            }
          } else {
            if (res?.body?.status === "expired") {
              setAcceptQuote("expired");
            } else {
              setAcceptQuote("action_expired");
            }
          }
          break;
        case "decline":
          setIsDeclineLoading(true);
          let result: any = await dispatch(
            quotesDetailsDecline(params)
          ).unwrap();
          if (result.body?.success) {
            setInvoiceId(result?.body?.data?.invoice_id);
            setAcceptQuote(value);
            const payload = {
              email: userData?.data?.accountData?.email,
              clusterid: params.clusterid,
              quote_slug: params.id,
              email_type: "quote_update",
            };
            await dispatch(emailDeclineQuotes(payload));
          } else {
            if (result?.body?.status === "expired") {
              setAcceptQuote("expired");
            } else {
              setAcceptQuote("action_failed");
            }
          }
          break;
        default:
          console.log("value =====", value);
      }
    } catch (error) {
      console.log("OnSubmit error =====", error);
    } finally {
      setIsDeclineLoading(false);
      setIsAcceptLoading(false);
    }
  };

  useEffect(() => {
    if (
      type &&
      quoteData &&
      (type === "accept" || type === "decline") &&
      quoteData?.status_quote?.toLowerCase() !== "accepted" &&
      quoteData?.status_quote?.toLowerCase() !== "declined"
    ) {
      if (type === "accept" || type === "decline") {
        OnSubmit(type);
      }
    }
  }, [type, quoteData]);

  return (
    <div
      className={` w-full lg:h-full lg:grid grid-cols-2 `}
      {...testAttrs("page", "Quote")}
    >
      <MessageToast
        variant="warning"
        content={mismatchNotice}
        width="w-full max-w-[442px] px-5"
        className="!right-1/2 !translate-x-1/2"
        onCloseClick={() => setMismatchNotice(null)}
      />
      {/* PAYMENT DESCRIPTION PART */}
      <div
        style={{ backgroundColor: brandColor }}
        className="relative lg:h-[var(--shell-vh)]"
        {...testAttrs("details-panel", "Quote details")}
      >
        {acceptQuote !== "decline" && acceptQuote !== "accept" && (
          <div
            className={`lg:absolute hidden lg:top-0 w-full lg:flex justify-between items-center z-10 ${
              isExpired(quoteData?.expiry_date || "") ||
              acceptQuote === "expired"
                ? "bg-lightRed2"
                : "bg-white"
            } lg:px-6 py-[6px] border-r-[2px] border-bgSecondary px-5`}
            {...testAttrs("desktop-expiry-banner", "Quote expiry")}
          >
            <p className="lg:text-base text-sm font-medium text-txtPrimary w-[50%] break-words">
              Quote{" "}
              {isExpired(quoteData?.expiry_date || "") ||
              acceptQuote === "expired"
                ? "expired"
                : "expires"}{" "}
              on{" "}
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                <span
                  className={`font-bold inline-block whitespace-nowrap ${
                    isExpired(quoteData?.expiry_date || "") ||
                    acceptQuote === "expired"
                      ? "text-red"
                      : "text-btnPrimaryActive"
                  } `}
                >
                  {formatPaymentListDate(
                    quoteData?.expiry_date || "",
                    "",
                    "long"
                  )}
                </span>
              )}
            </p>
            <Countdown
              isLoading={isLoading}
              seconds={seconds}
              isExpired={
                isExpired(quoteData?.expiry_date || "") ||
                acceptQuote === "expired"
              }
            />
          </div>
        )}

        <div
          className={`classScroll w-full lg:mt-[50px]  mt-[60px]
            lg:h-[calc(var(--shell-vh)-100px)] max-h-[calc(var(--shell-vh)-120px)]`}
          {...testAttrs("details-scroll", "Quote details scroll area")}
        >
          <div className="flex flex-row justify-center lg:mt-[7%] h-fit">
            {acceptQuote !== "decline" && acceptQuote !== "accept" && (
              <div
                className={`lg:hidden w-full flex justify-between gap-2 items-center mb-2 ${
                  isExpired(quoteData?.expiry_date || "") ||
                  acceptQuote === "expired"
                    ? "bg-lightRed2"
                    : "bg-white"
                }   py-[6px] px-5`}
                {...testAttrs("mobile-expiry-banner", "Quote expiry")}
              >
                <p className="lg:text-base text-sm font-medium text-txtPrimary w-[50%] break-words">
                  Quote{" "}
                  {isExpired(quoteData?.expiry_date || "") ||
                  acceptQuote === "expired"
                    ? "expired"
                    : "expires"}{" "}
                  on{" "}
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    <span
                      className={`font-bold inline-block whitespace-nowrap ${
                        isExpired(quoteData?.expiry_date || "") ||
                        acceptQuote === "expired"
                          ? "text-red"
                          : "text-btnPrimaryActive"
                      } `}
                    >
                      {formatPaymentListDate(
                        quoteData?.expiry_date || "",
                        "",
                        "long"
                      )}
                    </span>
                  )}
                </p>
                <Countdown
                  isLoading={isLoading}
                  seconds={seconds}
                  isExpired={
                    isExpired(quoteData?.expiry_date || "") ||
                    acceptQuote === "expired"
                  }
                />
              </div>
            )}
          </div>
          <div className="flex justify-center lg:mt-[7%] h-fit px-5 pt-1 pb-5">
            <div
              className={`flex flex-col items-start lg:gap-10 gap-4 w-full lg:max-w-[380px]`}
              {...testAttrs("summary", "Quote summary")}
            >
              <div className="lg:flex hidden items-center gap-[10px]">
                {isLoading ? (
                  <Skeleton width={100} height={35} />
                ) : (
                  <div className="h-16 lg:h-16 flex justify-end">
                    <img
                      src={paymentLogo}
                      alt="Company logo"
                      className="animate__animated animate__fadeIn object-contain"
                    />
                  </div>
                )}
              </div>
              {/* PAYMENT TYPE AND AMOUNT */}
              <div className={`flex flex-col items-start w-full`}>
                <span className="w-full flex justify-between items-center">
                  <div
                    className={` ${
                      isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                    } font-bold lg:text-xl text-lg leading-[26.04px]`}
                  >
                    Quote
                  </div>
                  <div
                    className={`lg:hidden text-base font-medium underline ${
                      isDark
                        ? "text-darkTxtBlue hover:!text-darkBlueHover"
                        : "text-blue hover:!text-btnPrimaryHover"
                    } cursor-pointer`}
                    role="button"
                    tabIndex={0}
                    {...testAttrs(
                      "toggle-details",
                      showDetails ? "Hide quote details" : "View quote details"
                    )}
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails ? "Hide details" : "View details"}
                  </div>
                </span>
                <div className="flex justify-between w-full lg:mt-4 mt-6 relative">
                  <div className="flex gap-6">
                    <div
                      className={`font-bold lg:leading-[65.1px] leading-[41.66px] ${
                        isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                      } lg:text-[50px] text-[32px]`}
                    >
                      {formatCurrency(quoteData?.total_amount) || "$0.00"}
                    </div>
                  </div>

                  <div className={`absolute right-0 lg:-top-[26px] -top-2  `}>
                    {!isLoading && (
                      <img
                        src={qrCode || ScannerImg}
                        alt="Quote QR code"
                        className={`lg:h-[90px] lg:w-[90px] h-[60px] w-[60px]  ${
                          isDark ? "invert" : "mix-blend-multiply"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
              <span
                className={`drawer ${
                  showDetails && "open"
                } flex flex-col w-full gap-5`}
                {...testAttrs("account-details", "Quote account details")}
              >
                {/* LINE */}
                <div className="h-[1px] bg-strokeActive lg:w-[380px] w-full" />
                {/* ACCOUNT DETAILS */}
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex flex-col gap-3 lg:max-w-[380px] w-full">
                    <div className="flex justify-between items-center">
                      <div
                        className={`leading-[26.04px] ${
                          isDark
                            ? "text-darkTxtBlue"
                            : "text-blue hover:!text-btnPrimaryHover"
                        } font-medium text-xl`}
                      >
                        {isLoading ? (
                          <Skeleton width={150} />
                        ) : (
                          (userData?.data?.accountData?.first_name || "") +
                          " " +
                          (userData?.data?.accountData?.last_name || "")
                        )}
                      </div>
                      <div
                        className={`text-sm ${
                          isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                        }`}
                      >
                        {isLoading ? (
                          <Skeleton width={80} />
                        ) : (
                          userData?.data?.accountData?.accountid
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <div className="grid grid-cols-2 w-full">
                        <div
                          className={`leading-[20px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-sm text-left`}
                        >
                          Balance
                        </div>
                        <div
                          className={`leading-[20px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-sm text-right`}
                        >
                          Description
                        </div>
                      </div>
                      <div className="grid grid-cols-2 w-full">
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-left`}
                        >
                          {isLoading ? (
                            <Skeleton width={70} />
                          ) : (
                            <>
                              {formatCurrency(
                                userData?.data?.accountData?.balance
                              ) || "$0.00"}
                            </>
                          )}
                        </div>
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-right`}
                        >
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            (userData?.data?.accountData?.description ?? "--")
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* LINE */}
                  <div className="h-[1px] bg-strokeActive lg:w-[380px] w-full" />
                  <div className="flex flex-col gap-3 lg:max-w-[380px] w-full">
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex justify-between items-center w-full">
                        <div
                          className={`leading-[16px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-[10px] uppercase text-left`}
                        >
                          Quote Number
                        </div>
                        <div
                          className={`leading-[16px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-[10px] uppercase text-right`}
                        >
                          Valid Until
                        </div>
                      </div>
                      <div className="flex justify-between items-center w-full">
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-left`}
                        >
                          {isLoading ? (
                            <Skeleton width={70} />
                          ) : (
                            <>#{quoteData?.quote_number}</>
                          )}
                        </div>
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-right`}
                        >
                          {isLoading ? (
                            <Skeleton width={70} />
                          ) : (
                            <>
                              {formatDisplayDate(
                                quoteData?.expiry_date ?? quoteData?.due,
                                "MM/DD/YYYY"
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </span>
              <Footer
                style={{ backgroundColor: brandColor }}
                className={`lg:!flex !hidden !w-[30%] max-w-[380px] !justify-start  ${
                  isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="lg:h-full"
        {...testAttrs("action-panel", "Quote actions")}
      >
        <QuotesList
          quoteData={quoteData}
          isLoading={isLoading}
          OnSubmit={OnSubmit}
          acceptQuote={acceptQuote}
          isFutureSub={isFutureSub}
          setIsPreview={setIsPreview}
          setIsPreviewId={setIsPreviewId}
          isAcceptLoading={isAcceptLoading}
          isDeclineLoading={isDeclineLoading}
          invoiceId={invoiceId}
        />
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setIsPreviewId("");
        }}
        overlayColor="#000"
      >
        <div className="contents" {...testAttrs("invoice-drawer")}>
          <InvoicePreview
            id={isPreviewId}
            slug={isPreviewId}
            setId={setIsPreviewId}
            setIsPreview={setIsPreview}
            isPreview={isPreview}
            isSlug={true}
          />
        </div>
      </Drawer>
    </div>
  );
};

const Quotes = () => (
  <TestIdScope scope="quote">
    <QuotesContent />
  </TestIdScope>
);

export default Quotes;
